import { api } from '@/lib/api';
import { keepPreviousData, queryOptions } from '@tanstack/react-query';
import { queryKeys } from '../queryKeys';
import type {
  OrderFilters,
  OrderHistoryFilters,
  OrderHistoryListResponse,
  OrderResponse,
  OrdersResponse,
} from './orderTypes';

export const getOrdersQuery = (page = 1, filters?: OrderFilters) =>
  queryOptions({
    queryKey: queryKeys.orders.all(
      page,
      filters as Record<string, unknown> | undefined,
    ),
    queryFn: async () => {
      const searchParams: Record<string, string | number> = { page };

      if (filters?.state) {
        searchParams.state = filters.state;
      }
      if (filters?.date_from) {
        searchParams.date_from = filters.date_from;
      }
      if (filters?.date_to) {
        searchParams.date_to = filters.date_to;
      }
      if (filters?.doctor_id) {
        searchParams.doctor_id = filters.doctor_id;
      }
      if (filters?.relation_type) {
        searchParams.relation_type = filters.relation_type;
      }
      if (filters?.lot_number) {
        searchParams.lot_number = filters.lot_number;
      }

      const response = await api
        .get('orders', { searchParams })
        .json<OrdersResponse>();
      return response;
    },
    placeholderData: keepPreviousData,
  });

export const getOrderQuery = (orderId: number) =>
  queryOptions({
    queryKey: queryKeys.orders.order(orderId),
    queryFn: async () => {
      const response = await api.get(`orders/${orderId}`).json<OrderResponse>();
      return response;
    },
  });

export interface OrderHistoryResponse {
  not_started: number;
  in_progress: number;
  done: number;
}

export const getOrderHistoryQuery = () =>
  queryOptions({
    queryKey: queryKeys.orders.history(),
    queryFn: async () => {
      const response = await api
        .get('orders/history')
        .json<OrderHistoryResponse>();
      return response;
    },
  });

export const getOrderHistoryListQuery = (
  page = 1,
  filters?: OrderHistoryFilters,
) =>
  queryOptions({
    queryKey: [queryKeys.orders.index, 'history-list', page, filters],
    queryFn: async () => {
      const searchParams: Record<string, string | number> = { page };

      if (filters?.state) {
        searchParams.state = filters.state;
      }
      if (filters?.date_from) {
        searchParams.date_from = filters.date_from;
      }
      if (filters?.date_to) {
        searchParams.date_to = filters.date_to;
      }
      if (filters?.search) {
        searchParams.search = filters.search;
      }

      const response = await api
        .get('orders/history', { searchParams })
        .json<OrderHistoryListResponse>();
      return response;
    },
    placeholderData: keepPreviousData,
  });

export const getUserOrdersQuery = (userId: number, page = 1) =>
  queryOptions({
    queryKey: [queryKeys.orders.index, 'user-orders', userId, page],
    queryFn: async () => {
      const response = await api
        .get(`orders/user/${userId}`, { searchParams: { page } })
        .json<OrdersResponse>();
      return response;
    },
  });
